// useSelection — tiny Set-backed selection state for Standup multi-select.
//
// Used by Phase 6b bulk actions. Intentionally dumb: it holds a Set<string>
// of ids and exposes toggle / select / clear / has / count helpers. No
// persistence, no URL sync — the selection resets on reload and on route
// change. That's deliberate: bulk actions are an in-session decision batch,
// not a user-facing filter.
import { useCallback, useMemo, useState } from 'react';

export type Selection = {
  ids: ReadonlySet<string>;
  count: number;
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  select: (ids: string[]) => void;
  clear: () => void;
};

export function useSelection(): Selection {
  const [ids, setIds] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const select = useCallback((newIds: string[]) => {
    setIds(new Set(newIds));
  }, []);

  const clear = useCallback(() => {
    setIds(new Set());
  }, []);

  const has = useCallback((id: string) => ids.has(id), [ids]);

  return useMemo(
    () => ({
      ids,
      count: ids.size,
      has,
      toggle,
      select,
      clear,
    }),
    [ids, has, toggle, select, clear],
  );
}
